import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ProductCarousel.css';
import { useCart } from '../../Context/Cartcontext';
import { getImageUrl } from '../../utils/imageUtils';

const ProductsCarousel = () => {
  // State for both sections
  const [specialOffers, setSpecialOffers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [activeTab, setActiveTab] = useState('special'); // 'special' or 'new'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";
  
  // Carousel states
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isManual, setIsManual] = useState(false);
  const [itemsPerSlide, setItemsPerSlide] = useState(3);

  // Toast state
  const [toast, setToast] = useState({ show: false, message: '' });
  const { addToCart } = useCart();

  // Fetch Special Offers
  useEffect(() => {
    const fetchSpecialOffers = async () => {
      try {
        const response = await fetch(`${baseURL}/api/products/getproductbytype/special_offer`);
        const data = await response.json();
        if (data.success) {
          setSpecialOffers(data.products);
        } else {
          console.error('Failed to fetch special offers');
        }
      } catch (error) {
        console.error("Error fetching special offer products:", error);
        setError('Error fetching special offers');
      }
    };

    fetchSpecialOffers();
  }, []);

  // Fetch New Arrivals
  useEffect(() => {
    const fetchNewArrivals = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${baseURL}/api/products/getproductbytype/new_arrival`);
        const data = await response.json();
        if (data.success) {
          setNewArrivals(data.products);
        } else {
          setError('Failed to fetch new arrivals');
        }
      } catch (err) {
        setError('Error connecting to server');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchNewArrivals();
  }, []);

  // Handle responsive items per slide
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setItemsPerSlide(3); // Large screens: 3 cards
      } else if (window.innerWidth >= 768) {
        setItemsPerSlide(2); // Medium screens: 2 cards
      } else {
        setItemsPerSlide(1); // Small screens: 1 card
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get current products based on active tab
  const getCurrentProducts = () => {
    return activeTab === 'special' ? specialOffers : newArrivals;
  };

  const currentProducts = getCurrentProducts();
  const totalSlides = Math.max(1, Math.ceil(currentProducts.length / itemsPerSlide));

  // Auto-scroll carousel
  useEffect(() => {
    if (isManual || currentProducts.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
    }, 6000);
    return () => clearInterval(interval);
  }, [isManual, totalSlides, currentProducts.length, itemsPerSlide]);

  // Reset carousel when tab changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [activeTab]);

  // Hide toast after 3 seconds
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const handleIndicatorClick = (index) => {
    setCurrentIndex(index);
    setIsManual(true);
    setTimeout(() => setIsManual(false), 6000);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const calculateDiscount = (regularPrice, offerPrice) => {
    return Math.round(((regularPrice - offerPrice) / regularPrice) * 100);
  };

  const isOfferCurrentlyValid = (validFrom, validUntil) => {
    const now = new Date();
    const from = new Date(validFrom);
    const until = new Date(validUntil);
    return now >= from && now <= until;
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Create cart item with appropriate price based on tab
    const cartItem = {
      id: product._id,
      name: product.name,
      price: product.offerPrice,
      imageUrl: product.imageUrl,
      type: activeTab === 'special' ? 'offer' : 'newarrival',
    };
    
    addToCart(cartItem);
    setToast({
      show: true,
      message: `${product.name} added to cart successfully!`
    });
  };

  // Get the appropriate route based on active tab
  const getProductRoute = (productId) => {
    return activeTab === 'special' ? `/offer/${productId}` : `/newarrivals/${productId}`;
  };

  // Get current slide items
  const getCurrentSlideItems = () => {
    if (currentProducts.length === 0) return [];
    
    const startIndex = currentIndex * itemsPerSlide;
    const endIndex = startIndex + itemsPerSlide;
    
    if (endIndex <= currentProducts.length) {
      return currentProducts.slice(startIndex, endIndex);
    }
    
    const remainingItems = currentProducts.slice(startIndex);
    const neededItems = itemsPerSlide - remainingItems.length;
    const wrappedItems = currentProducts.slice(0, neededItems);
    
    return [...remainingItems, ...wrappedItems];
  };

  const currentSlideItems = getCurrentSlideItems();

  if (loading) {
    return <div className="loading-container">Loading products...</div>;
  }

  if (error) {
    return <div className="error-container">{error}</div>;
  }

  return (
    <div className="combined-carousel-container">
      {/* Toast Notification */}
      {toast.show && (
        <div className="toast-notification">
          {toast.message}
        </div>
      )}

      {/* Tab Headers */}
      <div className="tab-headers">
        <div className="tab-buttons">
          <button
            onClick={() => handleTabChange('special')}
            className={`tab-button ${activeTab === 'special' ? 'active' : ''}`}
          >
            SPECIAL OFFERS <span className="tab-icon">%</span>
          </button>
          <button
            onClick={() => handleTabChange('new')}
            className={`tab-button ${activeTab === 'new' ? 'active' : ''}`}
          >
            NEW ARRIVALS <span className="tab-icon">⭐</span>
          </button>
        </div>
      </div>

      {/* Products Display */}
      {currentProducts.length > 0 && (
        <div className="carousel-container">
          <div className={`products-wrapper items-${itemsPerSlide}`}>
            {currentSlideItems.map((product, index) => {
              const discount = activeTab === 'special' 
                ? product.discount 
                : calculateDiscount(product.regularPrice, product.offerPrice);
              
              let isActive;
              if (activeTab === 'special') {
                isActive = product.stock > 0 && isOfferCurrentlyValid(product.validFrom, product.validUntil);
              } else {
                isActive = product.stock > 0;
              }

              return (
                <Link
                  key={`${product._id}-${currentIndex}-${index}`}
                  to={getProductRoute(product._id)}
                  className="custom-product-link"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="custom-card card">
                    {/* Discount Badge */}
                    <div className={`custom-discount-badge position-absolute ${activeTab === 'special' ? 'special-badge' : 'new-badge'}`}>
                      {discount}% OFF
                    </div>

                    {/* Product Image */}
                    <div className="image-container">
                      <img
                        src={getImageUrl(product.imageUrl)}
                        alt={product.name}
                        className="custom-card-img-top"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="no-image-placeholder">
                        No Image
                      </div>
                    </div>

                    {/* Product Details */}
                    <div className="custom-card-body">
                      <h5 className="custom-card-title">
                        {product.name}
                      </h5>

                      <p className="custom-offer-label">
                        {activeTab === 'special' ? '- Offer -' : '- New Arrival -'}
                      </p>

                      <h4 className="custom-offer-price">
                        RS. {product.offerPrice.toLocaleString()} 
                      </h4>

                      <p className="custom-regular-price">
                        (RS. {product.regularPrice.toLocaleString()})
                      </p>

                      {/* Stock Status and Add to Cart */}
                      <div className="custom-stock-wrapper">
                        <span className={`custom-status-badge ${isActive ? 'bg-success' : 'bg-danger'}`}>
                          {isActive ? (activeTab === 'special' ? 'Active' : 'In Stock') : (activeTab === 'special' ? 'Inactive' : 'Out of Stock')}
                        </span>
                        
                        <button
                          onClick={(e) => handleAddToCart(e, product)}
                          disabled={!isActive}
                          className="custom-addtocart-btn"
                        >
                          Add to Cart
                        </button>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Carousel Indicators */}
          {totalSlides > 1 && (
            <div className="custom-carousel-indicators">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <span
                  key={index}
                  onClick={() => handleIndicatorClick(index)}
                  className={`custom-indicator-dot ${index === currentIndex ? 'active' : ''}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductsCarousel;